import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import axiosInstance from "../../utils/axiosInstance";

const initialState = {
  squads: [],
  allSquads: [], // All squads for competition ranking
  hasMore: true,
  page: 1,
  // M2: the cohort the held rows belong to. A read for another cohort clears
  // them and the page first, and a late reply for any cohort but the newest
  // requested one is dropped.
  cohortId: null,
};

const cohortIdOf = (value) => {
  const n = Number(value);
  return value !== null &&
    value !== undefined &&
    Number.isSafeInteger(n) &&
    n > 0
    ? n
    : null;
};

/* M2: there is no default cohort. A read without the active delivery
   context's cohort is not sent (it used to fall back to cohort 1, which showed
   another cohort's standings). */
const hasCohort = (arg) => cohortIdOf(arg?.cohortID) !== null;

const enterCohortScope = (state, cohortID) => {
  const cohortId = cohortIdOf(cohortID);
  if (state.cohortId === cohortId) return;
  const previous = state.cohortId;
  state.cohortId = cohortId;
  // The first read of a session only names its cohort; rows and the page
  // are reset when the cohort actually changes under held rows.
  if (previous === null) return;
  state.squads = [];
  state.allSquads = [];
  state.hasMore = true;
  state.page = 1;
};

const isCurrentCohort = (state, action) =>
  cohortIdOf(action.meta?.arg?.cohortID) === state.cohortId;

export const fetchLeaderboard = createAsyncThunk(
  "leaderboard/fetchLeaderboard",
  async ({ page = 1, cohortID }) => {
    const response = await axiosInstance(
      `/squads?per_page=10&page=${page}&cohort_id=${cohortID}&_sort_by=total_points&_order=desc`,
      "get",
    );

    return {
      data: response.data.data,
      hasMore: response.data.meta.current_page < response.data.meta.last_page,
    };
  },
  { condition: hasCohort },
);

// Fetch all squads for competition ranking widget (larger per_page to get all)
export const fetchAllSquadsForRanking = createAsyncThunk(
  "leaderboard/fetchAllSquadsForRanking",
  async ({ cohortID }) => {
    const response = await axiosInstance(
      `/squads?per_page=200&page=1&cohort_id=${cohortID}&_sort_by=total_points&_order=desc`,
      "get",
    );
    return response.data.data;
  },
  { condition: hasCohort },
);

const leatherboardSlice = createSlice({
  name: "leatherboard",
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    updateLeaderboardPoints: (state, action) => {
      const { squad_id, points } = action.payload ?? {};
      /**
       * W4-F1 GUARD: THIS REDUCER ADDS A DELTA, AND THE ECONOMY CARRIES NONE.
       *
       * The Wave 4 economy message carries `squad_points`, an ABSOLUTE
       * normalized standing, and no `points` field at all (technical brief
       * 4.4). Read through the line below that is `undefined || 0`: harmless
       * today, and a silently wrong sum the moment somebody wires
       * `squad_points` in because the two names nearly match.
       *
       * For a versioned cohort the SELF READ is the sole authority for squad
       * standing, so an economy message must move nothing here by name. And a
       * missing delta returns BEFORE the map and the sort: rebuilding and
       * re-sorting the whole table to add zero is observable work on a message
       * this reducer has refused, because a re-sort reorders ties.
       */
      if (action.payload?.type === "economy") return;
      if (points === null || points === undefined) return;
      if (!Number.isFinite(Number(points))) return;
      const pointsEarned = Number(points);

      // Update allSquads (used by competition widget)
      state.allSquads = state.allSquads
        .map((squad) => {
          if (squad.id === squad_id) {
            return {
              ...squad,
              total_points: (squad.total_points || 0) + pointsEarned,
            };
          }
          return squad;
        })
        .sort((a, b) => (b.total_points || 0) - (a.total_points || 0));

      // Update squads (paginated leaderboard)
      state.squads = state.squads
        .map((squad) => {
          if (squad.id === squad_id) {
            return {
              ...squad,
              total_points: (squad.total_points || 0) + pointsEarned,
            };
          }
          return squad;
        })
        .sort((a, b) => (b.total_points || 0) - (a.total_points || 0));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchLeaderboard.pending, (state, action) => {
      enterCohortScope(state, action.meta?.arg?.cohortID);
    });
    builder.addCase(fetchAllSquadsForRanking.pending, (state, action) => {
      enterCohortScope(state, action.meta?.arg?.cohortID);
    });
    builder.addCase(fetchLeaderboard.fulfilled, (state, action) => {
      if (!isCurrentCohort(state, action)) return;
      if (state.page === 1) {
        state.squads = action.payload.data;
      } else {
        state.squads = [...state.squads, ...action.payload.data];
      }
      state.hasMore = action.payload.hasMore;
    });
    builder.addCase(fetchAllSquadsForRanking.fulfilled, (state, action) => {
      if (!isCurrentCohort(state, action)) return;
      state.allSquads = action.payload;
    });
  },
});

export const { setPage, updateLeaderboardPoints } = leatherboardSlice.actions;

export default leatherboardSlice.reducer;
